import type { RedlineChapter } from '@ditto/shared';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FC, useRef, useState, useEffect } from 'react';

interface RedlineChaptersPanelProps {
  chapters: RedlineChapter[];
}

const DiffStats: FC<{ added: number; removed: number }> = ({
  added,
  removed,
}) => {
  if (added === 0 && removed === 0) return null;

  return (
    <span className="text-sm font-bold whitespace-nowrap">
      {added > 0 && (
        <span className="text-[#25c329] tracking-[0.07px]">+{added}</span>
      )}
      {added > 0 && removed > 0 && <span>, </span>}
      {removed > 0 && (
        <span className="text-red-500 tracking-[0.07px]">-{removed}</span>
      )}
    </span>
  );
};

const TruncatedTitle: FC<{ title: string; depth: number }> = ({
  title,
  depth,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [title]);

  const content = (
    <span
      ref={textRef}
      className="block truncate"
      style={{ paddingLeft: `${depth * 21}px` }}
    >
      <span className="mr-1.5">•</span>
      {title}
    </span>
  );

  if (!isTruncated) return content;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[400px]">
        {title}
      </TooltipContent>
    </Tooltip>
  );
};

const ChapterRow: FC<{
  chapter: RedlineChapter;
  depth: number;
  childrenMap: Map<string | null, RedlineChapter[]>;
}> = ({ chapter, depth, childrenMap }) => {
  const children = childrenMap.get(chapter.id) ?? [];
  const hasChanges = chapter.charsAdded > 0 || chapter.charsRemoved > 0;

  return (
    <>
      <div
        className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hasChanges ? '' : 'opacity-40'}`}
      >
        <div className="flex-1 min-w-0 text-sm font-medium text-neutral-800 leading-[1.5]">
          <TruncatedTitle title={chapter.title} depth={depth} />
        </div>
        <DiffStats added={chapter.charsAdded} removed={chapter.charsRemoved} />
      </div>
      {children.map(child => (
        <ChapterRow
          key={child.id}
          chapter={child}
          depth={depth + 1}
          childrenMap={childrenMap}
        />
      ))}
    </>
  );
};

export const RedlineChaptersPanel: FC<RedlineChaptersPanelProps> = ({
  chapters,
}) => {
  const childrenMap = new Map<string | null, RedlineChapter[]>();
  for (const ch of chapters) {
    const key = ch.parentId;
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(ch);
  }

  const topLevel = childrenMap.get(null) ?? [];

  return (
    <div className="flex flex-col gap-4 px-12 py-6 overflow-y-auto h-full">
      <p className="text-sm font-medium text-neutral-900 tracking-[0.07px]">
        Sections
      </p>
      <div className="flex flex-col">
        {topLevel.map(chapter => (
          <ChapterRow
            key={chapter.id}
            chapter={chapter}
            depth={0}
            childrenMap={childrenMap}
          />
        ))}
      </div>
    </div>
  );
};
